import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useScheduleEditorModalStore } from '~/stores/schedule-editor-modal-store';
import { useUserStore } from '~/stores/user-store';
import LocalDateTime from '~/lib/localdatetime';
import Content from '~/components/layout/Content';
import KaliaCalendar from '~/components/kalia-calendar/KaliaCalendar';
import type { KaliaEvent, KaliaResource } from '~/components/kalia-calendar/types';

import { keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';

import {
    StoreSelector,
} from './AppointmentsCalendar';

import { CalendarScheduleTimeBlockEntry } from '~/components/calendar/CalendarScheduleTimeBlockEntry';
import { ScheduleEntryForm } from '~/components/practitioners/new-schedule-manager/ScheduleEntryForm';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format, getISOWeek, isDate } from 'date-fns';
import { de } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';
import {
    ArrowLeft,
    ArrowRight,
    CalendarIcon,
    FilterIcon,
    Loader2,
    Plus,
    StickyNote,
    UserPlus,
    Wifi,
    WifiOff,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/components/ui/tabs';
import { Checkbox } from '~/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '~/components/ui/dialog';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '~/components/ui/alert-dialog';
import {
    AppointmentFilters,
    CalendarViewMode,
    Equipment,
    Practitioner,
    Room,
} from '~/stores/appointments-calendar-store';
import { AppointmentStatus } from '~/types/server-types/AppointmentStatus';

// ---------------------------------------------------------------------------
// Shared toolbar sub-components (duplicated from AppointmentsCalendar to
// avoid coupling to the FullCalendar-based page while it still exists)
// ---------------------------------------------------------------------------

const CalendarDatePickerWithRange = () => {
    const { selectedDateRange, setSelectedDateRange, viewMode } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedDateRange: state.selectedDateRange,
            setSelectedDateRange: state.setSelectedDateRange,
            viewMode: state.viewMode,
        }))
    );

    const dates: LocalDateTime[] = useMemo(() => {
        const dateArray: LocalDateTime[] = [];
        let currentDate = selectedDateRange.startDate.clone();
        while (currentDate <= selectedDateRange.endDate) {
            dateArray.push(currentDate);
            currentDate = currentDate.addDays(1);
        }
        return dateArray;
    }, [selectedDateRange]);

    const handleDateChange = useCallback(
        (newDate: Date | LocalDateTime | undefined) => {
            if (!newDate) return;

            let localNewDate: LocalDateTime;
            if (isDate(newDate)) {
                const newDateStr = format(newDate, 'yyyy-MM-dd');
                localNewDate = new LocalDateTime(`${newDateStr} 00:00`, 'Europe/Berlin');
            } else {
                localNewDate = newDate;
            }

            let startDate = localNewDate;
            let endDate: LocalDateTime;

            switch (viewMode) {
                case 'dayGridMonth':
                    startDate = startDate.setDayOfMonth(1);
                    endDate = startDate.clone().addMonths(1);
                    break;
                case 'timeGridWeek':
                    startDate = startDate.setDayOfWeek('monday');
                    endDate = startDate.clone().addDays(6);
                    break;
                case 'timeGridThreeDays':
                    endDate = startDate.clone().addDays(2);
                    break;
                case 'resourceTimeGridDay':
                    endDate = startDate;
                    break;
                default:
                    endDate = startDate;
                    break;
            }

            setSelectedDateRange({ startDate, endDate });
        },
        [setSelectedDateRange, viewMode]
    );

    const handlePrevDay = useCallback(() => {
        let dayOffset = 1;
        if (viewMode === 'timeGridThreeDays') dayOffset = 3;
        else if (viewMode === 'timeGridWeek') dayOffset = 7;
        else if (viewMode === 'dayGridMonth') dayOffset = 30;
        handleDateChange(selectedDateRange.startDate.clone().addDays(-dayOffset));
    }, [selectedDateRange.startDate, handleDateChange, viewMode]);

    const handleNextDay = useCallback(() => {
        let dayOffset = 1;
        if (viewMode === 'timeGridThreeDays') dayOffset = 3;
        else if (viewMode === 'timeGridWeek') dayOffset = 7;
        else if (viewMode === 'dayGridMonth') dayOffset = 30;
        handleDateChange(selectedDateRange.startDate.clone().addDays(dayOffset));
    }, [selectedDateRange.startDate, handleDateChange, viewMode]);

    return (
        <div className={cn('grid gap-2')}>
            <div className='flex items-center'>
                <Button variant='outline' size='icon' onClick={handlePrevDay} className='mr-2'>
                    <ArrowLeft className='h-4 w-4' />
                </Button>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            id='date'
                            variant='outline'
                            className={cn(
                                'w-[300px] justify-start text-left font-normal',
                                !selectedDateRange.startDate && 'text-muted-foreground'
                            )}
                        >
                            <CalendarIcon className='mr-2 h-4 w-4' />
                            {selectedDateRange.startDate ? (
                                selectedDateRange.endDate &&
                                selectedDateRange.startDate.valueOf() !== selectedDateRange.endDate.valueOf() ? (
                                    <>
                                        {format(
                                            toZonedTime(new Date(selectedDateRange.startDate.getDisplayDate()), 'Europe/Berlin'),
                                            'dd.MM.yyyy',
                                            { locale: de }
                                        )}
                                        -
                                        {format(
                                            toZonedTime(new Date(selectedDateRange.endDate.getDisplayDate()), 'Europe/Berlin'),
                                            'dd.MM.yyyy',
                                            { locale: de }
                                        )}
                                        , KW {getISOWeek(toZonedTime(new Date(selectedDateRange.startDate.getDisplayDate()), 'Europe/Berlin'))}
                                    </>
                                ) : (
                                    <>
                                        {format(
                                            toZonedTime(new Date(selectedDateRange.startDate.getDisplayDate()), 'Europe/Berlin'),
                                            'EEEE, dd. MMMM yyyy',
                                            { locale: de }
                                        )}
                                        , KW {getISOWeek(toZonedTime(new Date(selectedDateRange.startDate.getDisplayDate()), 'Europe/Berlin'))}
                                    </>
                                )
                            ) : (
                                <span>Pick a date</span>
                            )}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-auto p-0' align='start'>
                        <Calendar
                            mode='multiple'
                            locale={de}
                            defaultMonth={new Date(dates[0].getDisplayDateTime())}
                            selected={dates.map((d) =>
                                toZonedTime(new Date(d.getDisplayDate()), 'Europe/Berlin')
                            )}
                            onDayClick={handleDateChange}
                            numberOfMonths={2}
                            weekStartsOn={1}
                        />
                    </PopoverContent>
                </Popover>
                <Button variant='outline' size='icon' onClick={handleNextDay} className='ml-2'>
                    <ArrowRight className='h-4 w-4' />
                </Button>
            </div>
        </div>
    );
};

const TodayButton = () => {
    const { selectedDateRange, setSelectedDateRange } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedDateRange: state.selectedDateRange,
            setSelectedDateRange: state.setSelectedDateRange,
        }))
    );

    const handleTodayClick = () => {
        const today = LocalDateTime.now('Europe/Berlin');
        const currentDurationMs = selectedDateRange.endDate.valueOf() - selectedDateRange.startDate.valueOf();
        const currentDurationDays = Math.round(currentDurationMs / 1000 / 60 / 60 / 24);
        setSelectedDateRange({
            startDate: today,
            endDate: today.clone().addDays(currentDurationDays),
        });
    };

    return (
        <Button variant='outline' onClick={handleTodayClick} className='w-24'>
            Heute
        </Button>
    );
};

const ViewModeSelector = () => {
    const { viewMode, setViewMode, selectedDateRange, setSelectedDateRange } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            viewMode: state.viewMode,
            setViewMode: state.setViewMode,
            selectedDateRange: state.selectedDateRange,
            setSelectedDateRange: state.setSelectedDateRange,
        }))
    );

    const handleViewModeChange = (value: string) => {
        const newViewMode = value as CalendarViewMode;
        setViewMode(newViewMode);

        const currentStartDate = selectedDateRange.startDate;
        let newStartDate = currentStartDate.clone();
        let newEndDate: LocalDateTime;

        switch (newViewMode) {
            case 'dayGridMonth':
                newStartDate = newStartDate.setDayOfMonth(1);
                newEndDate = newStartDate.clone().addMonths(1);
                break;
            case 'timeGridWeek':
                newStartDate = newStartDate.setDayOfWeek('monday');
                newEndDate = newStartDate.clone().addDays(6);
                break;
            case 'timeGridThreeDays':
                newEndDate = newStartDate.clone().addDays(2);
                break;
            case 'resourceTimeGridDay':
                newEndDate = newStartDate.clone();
                break;
            default:
                newEndDate = newStartDate.clone();
                break;
        }

        setSelectedDateRange({ startDate: newStartDate, endDate: newEndDate });
    };

    return (
        <Tabs value={viewMode} onValueChange={handleViewModeChange}>
            <TabsList className='grid grid-cols-3'>
                <TabsTrigger value='resourceTimeGridDay'>Tag</TabsTrigger>
                <TabsTrigger value='timeGridThreeDays'>3 Tage</TabsTrigger>
                <TabsTrigger value='timeGridWeek'>Woche</TabsTrigger>
            </TabsList>
        </Tabs>
    );
};

const CalendarResourcesFilter = () => {
    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({ selectedStore: state.selectedStore }))
    );
    const {
        appointmentFilters,
        roomFilters,
        equipmentFilters,
        practitionerFilters,
        allRooms,
        allEquipment,
        allPractitioners,
        setAppointmentFilters,
        setRoomFilters,
        setEquipmentFilters,
        setPractitionerFilters,
    } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            appointmentFilters: state.appointmentFilters,
            roomFilters: state.roomFilters,
            equipmentFilters: state.equipmentFilters,
            practitionerFilters: state.practitionerFilters,
            allRooms: state.allRooms,
            allEquipment: state.allEquipment,
            allPractitioners: state.allPractitioners,
            setAppointmentFilters: state.setAppointmentFilters,
            setRoomFilters: state.setRoomFilters,
            setEquipmentFilters: state.setEquipmentFilters,
            setPractitionerFilters: state.setPractitionerFilters,
        }))
    );

    const [tempAppointmentFilters, setTempAppointmentFilters] = useState<AppointmentFilters>(appointmentFilters);
    const [tempRoomFilters, setTempRoomFilters] = useState<Set<string>>(roomFilters);
    const [tempEquipmentFilters, setTempEquipmentFilters] = useState<Set<string>>(equipmentFilters);
    const [tempPractitionerFilters, setTempPractitionerFilters] = useState<Set<string>>(practitionerFilters);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (open) {
            setTempAppointmentFilters(appointmentFilters);
            setTempRoomFilters(roomFilters);
            setTempEquipmentFilters(equipmentFilters);
            setTempPractitionerFilters(practitionerFilters);
        }
    }, [open, appointmentFilters, roomFilters, equipmentFilters, practitionerFilters]);

    const handleApplyFilters = () => {
        setAppointmentFilters(tempAppointmentFilters);
        setRoomFilters(tempRoomFilters);
        setEquipmentFilters(tempEquipmentFilters);
        setPractitionerFilters(tempPractitionerFilters);
        setOpen(false);
    };

    const toggleFilter = (
        currentFilters: Set<string>,
        setFilters: (value: Set<string>) => void,
        id: string
    ) => {
        const newFilters = new Set(currentFilters);
        if (newFilters.has(id)) newFilters.delete(id);
        else newFilters.add(id);
        setFilters(newFilters);
    };

    const toggleCategoryItems = (
        currentFilters: Set<string>,
        setFilters: (value: Set<string>) => void,
        itemIds: string[]
    ) => {
        const allIncluded = itemIds.every((id) => currentFilters.has(id));
        const newFilters = new Set(currentFilters);
        if (allIncluded) itemIds.forEach((id) => newFilters.delete(id));
        else itemIds.forEach((id) => newFilters.add(id));
        setFilters(newFilters);
    };

    const roomsByCategory = useMemo(() => {
        const grouped: Record<string, Room[]> = {};
        if (!selectedStore) return grouped;
        grouped['No Category'] = [];
        allRooms
            .filter((room) => room.storeId === selectedStore.id)
            .forEach((room) => {
                const cat = room.room_category?.name || 'No Category';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(room);
            });
        return grouped;
    }, [allRooms, selectedStore]);

    const equipmentByCategory = useMemo(() => {
        const grouped: Record<string, Equipment[]> = {};
        if (!selectedStore) return grouped;
        grouped['No Category'] = [];
        allEquipment
            .filter((eq) => eq.storeId === selectedStore.id)
            .forEach((eq) => {
                const cat = eq.equipment_category?.name || 'No Category';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(eq);
            });
        return grouped;
    }, [allEquipment, selectedStore]);

    const practitionersByCategory = useMemo(() => {
        const grouped: Record<string, Practitioner[]> = {};
        if (!selectedStore) return grouped;
        grouped['No Category'] = [];
        allPractitioners
            .filter((p) => p.storeIds.includes(selectedStore.id))
            .forEach((p) => {
                const cat = p.practitioner_category?.name || 'No Category';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(p);
            });
        return grouped;
    }, [allPractitioners, selectedStore]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant='outline' size='icon' className='relative'>
                    <FilterIcon className='h-4 w-4' />
                    {(roomFilters.size > 0 || equipmentFilters.size > 0 || practitionerFilters.size > 0) && (
                        <div className='absolute -top-2 -right-2 h-5 w-5 rounded-full bg-primary-500 flex items-center justify-center text-[10px] text-white font-medium'>
                            {roomFilters.size + equipmentFilters.size + practitionerFilters.size}
                        </div>
                    )}
                </Button>
            </DialogTrigger>
            <DialogContent className='sm:max-w-[600px]'>
                <DialogHeader>
                    <DialogTitle>Filter</DialogTitle>
                    <DialogDescription>Wähle, was du im Kalender anzeigen möchtest.</DialogDescription>
                </DialogHeader>
                <div className='flex flex-row gap-8'>
                    <Tabs defaultValue='appointments' className='mt-4'>
                        <TabsList className='grid grid-cols-3'>
                            <TabsTrigger value='rooms'>Räume</TabsTrigger>
                            <TabsTrigger value='equipment'>Geräte</TabsTrigger>
                            <TabsTrigger value='practitioners'>Behandler</TabsTrigger>
                        </TabsList>

                        <TabsContent value='rooms' className='max-h-[500px] overflow-y-auto'>
                            {Object.keys(roomsByCategory).length > 0 ? (
                                <div className='space-y-4 mt-2'>
                                    {Object.entries(roomsByCategory).map(([categoryName, rooms]) => {
                                        if (rooms.length === 0) return null;
                                        const ids = rooms.map((r) => r.id.toString());
                                        const allSelected = ids.every((id) => tempRoomFilters.has(id));
                                        return (
                                            <div key={categoryName} className='space-y-2'>
                                                <div className='flex items-center space-x-2 font-medium'>
                                                    <Checkbox
                                                        id={`room-category-${categoryName}`}
                                                        checked={allSelected && rooms.length > 0}
                                                        onCheckedChange={() => toggleCategoryItems(tempRoomFilters, setTempRoomFilters, ids)}
                                                    />
                                                    <label htmlFor={`room-category-${categoryName}`} className='text-sm font-bold'>
                                                        {categoryName} ({rooms.length})
                                                    </label>
                                                </div>
                                                <div className='pl-6 space-y-2'>
                                                    {rooms.map((room) => (
                                                        <div key={room.id} className='flex items-center space-x-2'>
                                                            <Checkbox
                                                                id={`room-${room.id}`}
                                                                checked={tempRoomFilters.has(room.id.toString())}
                                                                onCheckedChange={() => toggleFilter(tempRoomFilters, setTempRoomFilters, room.id.toString())}
                                                            />
                                                            <label htmlFor={`room-${room.id}`} className='text-sm font-medium leading-none'>
                                                                {room.name.length > 64 ? `${room.name.substring(0, 61)}...` : room.name}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className='text-sm text-muted-foreground mt-2'>No rooms available</p>
                            )}
                        </TabsContent>

                        <TabsContent value='equipment' className='max-h-[500px] overflow-y-auto'>
                            {Object.keys(equipmentByCategory).length > 0 ? (
                                <div className='space-y-4 mt-2'>
                                    {Object.entries(equipmentByCategory).map(([categoryName, items]) => {
                                        if (items.length === 0) return null;
                                        const ids = items.map((i) => i.id.toString());
                                        const allSelected = ids.every((id) => tempEquipmentFilters.has(id));
                                        return (
                                            <div key={categoryName} className='space-y-2'>
                                                <div className='flex items-center space-x-2 font-medium'>
                                                    <Checkbox
                                                        id={`eq-cat-${categoryName}`}
                                                        checked={allSelected && items.length > 0}
                                                        onCheckedChange={() => toggleCategoryItems(tempEquipmentFilters, setTempEquipmentFilters, ids)}
                                                    />
                                                    <label htmlFor={`eq-cat-${categoryName}`} className='text-sm font-bold'>
                                                        {categoryName} ({items.length})
                                                    </label>
                                                </div>
                                                <div className='pl-6 space-y-2'>
                                                    {items.map((eq) => (
                                                        <div key={eq.id} className='flex items-center space-x-2'>
                                                            <Checkbox
                                                                id={`eq-${eq.id}`}
                                                                checked={tempEquipmentFilters.has(eq.id.toString())}
                                                                onCheckedChange={() => toggleFilter(tempEquipmentFilters, setTempEquipmentFilters, eq.id.toString())}
                                                            />
                                                            <label htmlFor={`eq-${eq.id}`} className='text-sm font-medium leading-none'>
                                                                {eq.name.length > 64 ? `${eq.name.substring(0, 61)}...` : eq.name}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className='text-sm text-muted-foreground mt-2'>No equipment available</p>
                            )}
                        </TabsContent>

                        <TabsContent value='practitioners' className='max-h-[500px] overflow-y-auto'>
                            {Object.keys(practitionersByCategory).length > 0 ? (
                                <div className='space-y-4 mt-2'>
                                    {Object.entries(practitionersByCategory).map(([categoryName, practitioners]) => {
                                        if (practitioners.length === 0) return null;
                                        const ids = practitioners.map((p) => p.id.toString());
                                        const allSelected = ids.every((id) => tempPractitionerFilters.has(id));
                                        return (
                                            <div key={categoryName} className='space-y-2'>
                                                <div className='flex items-center space-x-2 font-medium'>
                                                    <Checkbox
                                                        id={`prac-cat-${categoryName}`}
                                                        checked={allSelected && practitioners.length > 0}
                                                        onCheckedChange={() =>
                                                            toggleCategoryItems(tempPractitionerFilters, setTempPractitionerFilters, ids)
                                                        }
                                                    />
                                                    <label htmlFor={`prac-cat-${categoryName}`} className='text-sm font-bold'>
                                                        {categoryName} ({practitioners.length})
                                                    </label>
                                                </div>
                                                <div className='pl-6 space-y-2'>
                                                    {practitioners.map((p) => {
                                                        const label = `${p.firstName} ${p.lastName}`;
                                                        return (
                                                            <div key={p.id} className='flex items-center space-x-2'>
                                                                <Checkbox
                                                                    id={`prac-${p.id}`}
                                                                    checked={tempPractitionerFilters.has(p.id.toString())}
                                                                    onCheckedChange={() =>
                                                                        toggleFilter(tempPractitionerFilters, setTempPractitionerFilters, p.id.toString())
                                                                    }
                                                                />
                                                                <label htmlFor={`prac-${p.id}`} className='text-sm font-medium leading-none'>
                                                                    {label.length > 64 ? `${label.substring(0, 61)}...` : label}
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div className='space-y-2'>
                                        <div className='flex items-center space-x-2 font-medium'>
                                            <label className='text-sm font-bold'>Other (2)</label>
                                        </div>
                                        <div className='pl-6 space-y-2'>
                                            {[
                                                { label: 'Nicht zugewiesen', id: 'unassigned' },
                                                { label: 'Einwirkzeit/Durchlaufzeit', id: 'waiting' },
                                            ].map((item) => (
                                                <div key={item.id} className='flex items-center space-x-2'>
                                                    <Checkbox
                                                        id={`prac-${item.id}`}
                                                        checked={tempPractitionerFilters.has(item.id)}
                                                        onCheckedChange={() =>
                                                            toggleFilter(tempPractitionerFilters, setTempPractitionerFilters, item.id)
                                                        }
                                                    />
                                                    <label htmlFor={`prac-${item.id}`} className='text-sm font-medium leading-none'>
                                                        {item.label}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className='text-sm text-muted-foreground mt-2'>No practitioners available</p>
                            )}
                        </TabsContent>
                    </Tabs>
                    <div className='flex flex-col gap-2'>
                        <span>Status</span>
                        <div className='space-y-2'>
                            {Object.values(AppointmentStatus).map((status) => {
                                const label = (status[0].toUpperCase() + status.slice(1).toLowerCase()).replaceAll('_', ' ');
                                return (
                                    <div key={`status-${status}`} className='flex items-center space-x-2'>
                                        <Checkbox
                                            id={`appt-status-${status}`}
                                            checked={tempAppointmentFilters.status.has(status)}
                                            onCheckedChange={(checked) => {
                                                const newSet = new Set(tempAppointmentFilters.status);
                                                if (checked === 'indeterminate' || !checked) newSet.delete(status);
                                                else newSet.add(status);
                                                setTempAppointmentFilters((prev) => ({ ...prev, status: newSet }));
                                            }}
                                        />
                                        <label className='text-sm font-medium leading-none'>{label}</label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <DialogFooter className='flex justify-between sm:justify-between'>
                    <div className='flex gap-2'>
                        <Button
                            variant='outline'
                            onClick={() => {
                                setTempAppointmentFilters((prev) => ({ ...prev, status: new Set() }));
                                setTempRoomFilters(new Set());
                                setTempEquipmentFilters(new Set());
                                setTempPractitionerFilters(new Set());
                            }}
                        >
                            Filter zurücksetzen
                        </Button>
                        <Button variant='outline' onClick={() => setOpen(false)}>
                            Abbrechen
                        </Button>
                    </div>
                    <Button onClick={handleApplyFilters}>Filter anwenden</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const IncomingAppointmentRequestsWidget = ({ storeId }: { storeId: number }) => {
    const { setMode, setAppointmentId, setNotifyCustomer } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            setMode: state.setMode,
            setAppointmentId: state.setAppointmentId,
            setNotifyCustomer: state.setNotifyCustomer,
        }))
    );
    const { setCreateOrModifyAppointmentDrawerOpen } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            setCreateOrModifyAppointmentDrawerOpen: state.setCreateOrModifyAppointmentDrawerOpen,
        }))
    );
    const { timezone } = useUserStore(useShallow((state) => ({ timezone: state.user?.timezone })));

    const DISPLAY_DATE_FORMAT = 'EEEE, dd. MMMM yyyy';
    const DISPLAY_TIME_FORMAT = 'HH:mm';
    const [open, setOpen] = useState(false);
    const trpcUtils = trpc.useUtils();

    const appointmentRequestsQuery = trpc.appointments.getIncomingAppointmentRequests.useQuery(
        { storeId },
        { refetchInterval: 10000 }
    );

    const acceptMutation = trpc.appointments.acceptAppointmentRequest.useMutation({
        onSuccess: () => {
            appointmentRequestsQuery.refetch();
            toast.success('Appointment was accepted');
            trpcUtils.appointments.getAppointmentsForScheduler.refetch();
        },
    });
    const declineMutation = trpc.appointments.declineAppointmentRequest.useMutation({
        onSuccess: () => {
            appointmentRequestsQuery.refetch();
            toast.success('Appointment was declined');
            trpcUtils.appointments.getAppointmentsForScheduler.refetch();
        },
    });

    const incomingAppointmentRequests = useMemo(
        () => appointmentRequestsQuery.data?.events || [],
        [appointmentRequestsQuery.data]
    );
    const numberOfRequests = incomingAppointmentRequests.length;

    const formatDateTime = (utcDateTime: string) => {
        if (!utcDateTime) return { date: 'N/A', time: 'N/A' };
        const localDateTime = dayjs.utc(utcDateTime).tz(timezone || 'Europe/Berlin');
        return {
            date: format(localDateTime.toDate(), DISPLAY_DATE_FORMAT, { locale: de }),
            time: localDateTime.format(DISPLAY_TIME_FORMAT),
        };
    };

    const handleAccept = async (appointmentId: string) => {
        if (acceptMutation.isPending) return;
        await acceptMutation.mutateAsync({ appointmentId });
        if (numberOfRequests === 1) setOpen(false);
    };
    const handleDecline = async (appointmentId: string) => {
        if (declineMutation.isPending) return;
        await declineMutation.mutateAsync({ appointmentId });
        if (numberOfRequests === 1) setOpen(false);
    };
    const handleEdit = (appointmentId: string) => {
        setMode('modify');
        setAppointmentId(appointmentId);
        setNotifyCustomer(false);
        setCreateOrModifyAppointmentDrawerOpen(true);
    };

    const formatDateTimeAgo = (utcDateTime: string) => {
        const now = new Date();
        const date = new Date(utcDateTime);
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
        if (seconds < 60) return `vor ${seconds} ${seconds === 1 ? 'Sekunde' : 'Sekunden'}`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `vor ${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `vor ${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
        const days = Math.floor(hours / 24);
        if (days < 7) return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
        const weeks = Math.floor(days / 7);
        if (weeks < 4) return `vor ${weeks} ${weeks === 1 ? 'Woche' : 'Wochen'}`;
        const months = Math.floor(days / 30);
        if (months < 12) return `vor ${months} ${months === 1 ? 'Monat' : 'Monaten'}`;
        const years = Math.floor(days / 365);
        return `vor ${years} ${years === 1 ? 'Jahr' : 'Jahren'}`;
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant='outline' size='icon' className='relative'>
                    <CalendarIcon className='h-4 w-4' />
                    {!open && numberOfRequests > 0 && (
                        <div className='absolute -top-2 -right-2 h-5 w-5 rounded-full bg-primary-500 flex items-center justify-center text-[10px] text-white font-medium'>
                            {numberOfRequests}
                        </div>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[420px] p-0' align='end'>
                <div className='flex flex-col gap-0.5 px-4 pt-3 pb-2'>
                    <h3 className='text-base font-semibold tracking-tight'>
                        Neue Terminanfragen ({numberOfRequests})
                    </h3>
                    <p className='text-xs text-muted-foreground'>
                        Zeitzone: {(timezone || 'Europe/Berlin').replace('_', ' ')}
                    </p>
                </div>
                <div className='max-h-[350px] overflow-y-auto px-4 pb-3'>
                    {appointmentRequestsQuery.isLoading ? (
                        <p className='text-sm text-muted-foreground text-center py-4'>Lade Anfragen...</p>
                    ) : numberOfRequests > 0 ? (
                        <div className='space-y-2'>
                            {incomingAppointmentRequests.map((event) => {
                                const { date: startDate, time: startTime } = formatDateTime(event.start);
                                const { time: endTime } = formatDateTime(event.end);
                                const referenceDate = event.rescheduledAt ?? event.createdAt;
                                const timeSinceAppointment = formatDateTimeAgo(referenceDate);
                                return (
                                    <div key={event.id} className='border rounded-lg p-2.5 space-y-0.5 bg-card'>
                                        <div className='flex justify-between items-start'>
                                            <h3 className='font-semibold text-sm'>
                                                {event.metadata.firstName} {event.metadata.lastName}
                                            </h3>
                                            <span className='text-[10px] text-muted-foreground'>{event.rescheduledAt ? `verschoben ${timeSinceAppointment}` : timeSinceAppointment}</span>
                                        </div>
                                        <p className='text-xs text-muted-foreground'>
                                            <span className='font-medium text-foreground'>Behandlung:</span> {event.title}
                                        </p>
                                        <p className='text-xs text-muted-foreground'>
                                            <span className='font-medium text-foreground'>Datum:</span> {startDate}
                                        </p>
                                        <p className='text-xs text-muted-foreground'>
                                            <span className='font-medium text-foreground'>Uhrzeit:</span> {startTime} - {endTime}
                                        </p>
                                        {event.metadata.customerEmail && (
                                            <p className='text-xs text-muted-foreground truncate' title={event.metadata.customerEmail}>
                                                <span className='font-medium text-foreground'>E-Mail:</span> {event.metadata.customerEmail}
                                            </p>
                                        )}
                                        {event.metadata.customerPhone && (
                                            <p className='text-xs text-muted-foreground'>
                                                <span className='font-medium text-foreground'>Tel.:</span> {event.metadata.customerPhone}
                                            </p>
                                        )}
                                        <div className='flex gap-2 pt-1.5'>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        size='sm'
                                                        variant='outline'
                                                        className='h-7 px-2 text-xs flex-1 bg-red-600 hover:bg-red-700 text-white'
                                                        disabled={declineMutation.isPending && declineMutation.variables?.appointmentId === event.metadata.appointmentId}
                                                    >
                                                        Ablehnen
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Bist du dir sicher?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Bist du dir sicher, dass du den Termin ablehnen möchtest und auf den Status &quot;DECLINED&quot; setzen möchtest?
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                        <AlertDialogAction asChild>
                                                            <Button
                                                                onClick={async () => await handleDecline(event.metadata.appointmentId)}
                                                                disabled={declineMutation.isPending && declineMutation.variables?.appointmentId === event.metadata.appointmentId}
                                                            >
                                                                Ablehnen
                                                            </Button>
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                            <Button
                                                size='sm'
                                                className='h-7 px-2 text-xs flex-1 bg-green-600 hover:bg-green-700'
                                                onClick={() => handleAccept(event.metadata.appointmentId)}
                                                disabled={acceptMutation.isPending && acceptMutation.variables?.appointmentId === event.metadata.appointmentId}
                                            >
                                                Annehmen
                                            </Button>
                                            <Button
                                                size='sm'
                                                variant='outline'
                                                className='h-7 px-2 text-xs flex-1 text-black'
                                                onClick={() => handleEdit(event.metadata.appointmentId)}
                                            >
                                                Bearbeiten
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground text-center py-4'>Keine neuen Terminanfragen.</p>
                    )}
                </div>
                <Button variant='outline' onClick={() => setOpen(false)} className='w-full'>
                    Schließen
                </Button>
            </PopoverContent>
        </Popover>
    );
};

const CreateAppointmentButton = () => {
    const { setCreateOrModifyAppointmentDrawerOpen } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            setCreateOrModifyAppointmentDrawerOpen: state.setCreateOrModifyAppointmentDrawerOpen,
        }))
    );
    const { reset, setNotifyCustomer } = useCreateOrModifyAppointmentStore(useShallow((state) => ({ reset: state.reset, setNotifyCustomer: state.setNotifyCustomer })));

    return (
        <Button
            variant='outline'
            onClick={() => {
                reset();
                setNotifyCustomer(true);
                setCreateOrModifyAppointmentDrawerOpen(true);
            }}
        >
            <Plus className='h-4 w-4 mr-2' />
            Termin erstellen
        </Button>
    );
};

// ---------------------------------------------------------------------------
// Calendar Legend – shows the meaning of the icon badges rendered on events
// ---------------------------------------------------------------------------

const CalendarLegend = () => (
    <div className='flex flex-row flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground'>
        <div className='flex items-center gap-1.5'>
            <div className='p-0.5 rounded-full bg-gray-600'>
                <Wifi className='w-3 h-3 text-[#eee]' />
            </div>
            <span>Online Buchung</span>
        </div>
        <div className='flex items-center gap-1.5'>
            <div className='p-0.5 rounded-full bg-gray-600'>
                <WifiOff className='w-3 h-3 text-[#eee]' />
            </div>
            <span>Manuelle Buchung</span>
        </div>
        <div className='flex items-center gap-1.5'>
            <div className='p-0.5 rounded-full bg-gray-600'>
                <StickyNote className='w-3 h-3 text-[#eee]' />
            </div>
            <span>Interne Kundennotiz / Terminnotiz vorhanden</span>
        </div>
        <div className='flex items-center gap-1.5'>
            <div className='p-0.5 rounded-full bg-gray-600'>
                <UserPlus className='w-3 h-3 text-[#eee]' />
            </div>
            <span>Neukunde</span>
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// Color Legend – shows the meaning of the appointment background colors
// ---------------------------------------------------------------------------

const COLOR_LEGEND_ITEMS = [
    { color: '#FBFBE6', label: 'RFN' },
    { color: '#FFFC92', label: 'Influencer' },
    { color: '#B0E313', label: 'IPL' },
    { color: '#BDD7A5', label: 'Standard' },
    { color: '#BBDACB', label: 'zusammenhängende Termine' },
    { color: '#B3FCF3', label: 'Dermatologische Services' },
    { color: '#B5CFE8', label: 'CO2-Laser (gescreent)' },
    { color: '#1931FD', label: 'Hautanalyse' },
    { color: '#FFE3F9', label: 'Infusionen' },
    { color: '#FC03DF', label: 'CO2-Laser (ohne Screening)' },
    { color: '#DEC8E2', label: 'Kosmetische Services' },
    { color: '#CE63FF', label: 'Gefäßlaser' },
    { color: '#FBE4B2', label: 'temporärer Termin-Blocker' },
    { color: '#4482fc', label: 'EMSCULPT' },
    { color: '#D94518', label: 'Problem-Termine (zu verschieben)' },
    { color: '#A80F10', label: 'Bluttests' },
] as const;

const ColorLegend = () => (
    <Popover>
        <PopoverTrigger asChild>
            <button className='text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors cursor-pointer'>
                Farbenlegende
            </button>
        </PopoverTrigger>
        <PopoverContent className='w-auto' align='start'>
            <div className='grid grid-cols-4 gap-x-4 gap-y-2.5'>
                {COLOR_LEGEND_ITEMS.map((item) => (
                    <div key={item.label} className='flex items-center gap-1.5'>
                        <div
                            className='w-4 h-4 rounded-sm shrink-0 border border-black/10'
                            style={{ backgroundColor: item.color }}
                        />
                        <span className='text-xs whitespace-nowrap'>{item.label}</span>
                    </div>
                ))}
            </div>
        </PopoverContent>
    </Popover>
);

// ---------------------------------------------------------------------------
// Schedule Editor Modal (no FullCalendar ref needed)
// ---------------------------------------------------------------------------

const ScheduleEditorModal = () => {
    const { open, scheduleId, type, clickedDateRange, setOpen, reset } = useScheduleEditorModalStore(
        useShallow((state) => ({
            open: state.open,
            scheduleId: state.scheduleId,
            type: state.type,
            clickedDateRange: state.clickedDateRange,
            setOpen: state.setOpen,
            reset: state.reset,
        }))
    );

    const trpcUtils = trpc.useUtils();
    const { timezone: userTimezone } = useUserStore(useShallow((state) => ({ timezone: state.user?.timezone })));
    const { selectedStore } = useAppointmentsCalendarStore(useShallow((state) => ({ selectedStore: state.selectedStore })));

    const practitionerScheduleEntryQuery = trpc.practitioners.getScheduleEntry.useQuery(
        { id: scheduleId || '' },
        { enabled: scheduleId !== null && type === 'practitioner' }
    );
    const equipmentScheduleEntryQuery = trpc.equipment.getScheduleEntry.useQuery(
        { id: scheduleId || '' },
        { enabled: scheduleId !== null && type === 'equipment' }
    );

    const scheduleEntry = useMemo(() => {
        if (type === 'practitioner') return practitionerScheduleEntryQuery?.data || null;
        if (type === 'equipment') return equipmentScheduleEntryQuery?.data || null;
        return null;
    }, [type, practitionerScheduleEntryQuery, equipmentScheduleEntryQuery]);

    const dataObjectId = useMemo(() => {
        if (scheduleEntry) {
            if ('practitionerId' in scheduleEntry) return scheduleEntry.practitionerId;
            if ('equipmentId' in scheduleEntry) return scheduleEntry.equipmentId;
        }
        return undefined;
    }, [scheduleEntry]);

    useEffect(() => {
        if (!open) reset();
    }, [open]);

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) reset();
                setOpen(o);
            }}
        >
            <DialogContent className='max-w-xl max-h-full overflow-y-scroll'>
                <DialogHeader>
                    <DialogTitle>Blocker bearbeiten</DialogTitle>
                </DialogHeader>
                <div className='space-y-4'>
                    {selectedStore && dataObjectId && type && scheduleEntry ? (
                        <ScheduleEntryForm
                            key={scheduleEntry.id ?? 'new'}
                            dataObjectId={dataObjectId}
                            dataObjectType={type}
                            overwriteEntryType='BLOCKED'
                            storeId={selectedStore.id}
                            userTimezone={userTimezone || 'Europe/Berlin'}
                            scheduleEntryId={scheduleEntry?.id ?? undefined}
                            selectedSlot={clickedDateRange ?? undefined}
                            onSuccess={() => {
                                setOpen(false);
                                reset();
                                Promise.all([
                                    trpcUtils.practitioners.getScheduleEntry.invalidate(),
                                    trpcUtils.equipment.getScheduleEntry.invalidate(),
                                    trpcUtils.appointments.getAppointmentsForScheduler.invalidate(),
                                ]);
                            }}
                            onCancel={() => {
                                setOpen(false);
                                reset();
                            }}
                        />
                    ) : (
                        <div className='w-[200px] h-[200px] flex items-center justify-center'>
                            <Loader2 className='animate-spin' />
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// KaliaCalendar wrapper – handles data fetching and event transformation
// ---------------------------------------------------------------------------

type KaliaCalendarWrapperProps = {
    resources: {
        id: string;
        title: string;
        storeId: number | null;
        storeIds: number[] | null;
        order: number;
    }[];
};

const KaliaCalendarWrapper = ({ resources }: KaliaCalendarWrapperProps) => {
    const { timezone } = useUserStore(useShallow((state) => ({ timezone: state.user?.timezone })));

    const {
        selectedStore,
        viewMode,
        selectedDateRange,
        appointmentFilters,
        roomFilters,
        equipmentFilters,
        practitionerFilters,
    } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            viewMode: state.viewMode,
            selectedDateRange: state.selectedDateRange,
            appointmentFilters: state.appointmentFilters,
            roomFilters: state.roomFilters,
            equipmentFilters: state.equipmentFilters,
            practitionerFilters: state.practitionerFilters,
        }))
    );

    const filteredResources = useMemo(() => {
        if (!resources || !selectedStore) return [];

        const storeResources = resources.filter((r) => !r.storeIds || r.storeIds.includes(selectedStore.id));
        const allFiltersEmpty = roomFilters.size === 0 && equipmentFilters.size === 0 && practitionerFilters.size === 0;
        if (allFiltersEmpty) return storeResources;

        return storeResources.filter((r) => {
            if (r.id.startsWith('practitioner-')) return practitionerFilters.has(r.id.replace('practitioner-', ''));
            if (r.id === 'unassigned' || r.id === 'waiting') return practitionerFilters.has(r.id);
            if (r.id.startsWith('room-')) return roomFilters.has(r.id.replace('room-', ''));
            if (r.id.startsWith('equipment-')) return equipmentFilters.has(r.id.replace('equipment-', ''));
            return false;
        });
    }, [resources, selectedStore, roomFilters, equipmentFilters, practitionerFilters]);

    const queryFilters = useMemo(
        () => ({
            storeId: selectedStore?.id || 0,
            dateRange: {
                startDate: selectedDateRange.startDate.toJson(),
                endDate: selectedDateRange.endDate.addDays(1).toJson(),
            },
            appointmentFilters: { ...appointmentFilters, status: Array.from(appointmentFilters.status) },
            practitionerFilters: practitionerFilters.size === 0 ? undefined : Array.from(practitionerFilters),
            roomFilters: roomFilters.size === 0 ? undefined : Array.from(roomFilters),
            equipmentFilters: equipmentFilters.size === 0 ? undefined : Array.from(equipmentFilters),
            mode: (viewMode === 'resourceTimeGridDay' ? 'steps' : 'appointments') as 'steps' | 'appointments',
        }),
        [selectedStore, selectedDateRange, appointmentFilters, practitionerFilters, roomFilters, equipmentFilters, viewMode]
    );

    const { data: appointmentsData } = trpc.appointments.getAppointmentsForScheduler.useQuery(queryFilters, {
        refetchInterval: 15_000,
        staleTime: 15_000,
        placeholderData: keepPreviousData,
        enabled: !!selectedStore?.id && selectedStore.id !== 0,
    });

    const isMultiDay = viewMode !== 'resourceTimeGridDay';
    const tz = timezone || 'Europe/Berlin';

    const dateColumns: KaliaResource[] = useMemo(() => {
        if (!isMultiDay) return [];

        const cols: KaliaResource[] = [];
        let current = selectedDateRange.startDate.clone();
        let order = 0;
        while (current <= selectedDateRange.endDate) {
            const dateStr = current.getDisplayDate();
            const d = dayjs(dateStr);
            cols.push({
                id: `date-${dateStr}`,
                title: d.format('dd, DD.MM.'),
                order: order++,
            });
            current = current.addDays(1);
        }
        return cols;
    }, [isMultiDay, selectedDateRange]);

    const kaliaEvents: KaliaEvent[] = useMemo(() => {
        const raw = appointmentsData?.events || [];
        const result: KaliaEvent[] = [];

        for (const event of raw) {
            let start: Date | null = null;
            let end: Date | null = null;
            try {
                start = LocalDateTime.deserialize(event.startLocalSerialized).toUTC();
                end = LocalDateTime.deserialize(event.endLocalSerialized).toUTC();
            } catch {
                continue;
            }
            if (!start || !end) continue;

            if (viewMode !== 'resourceTimeGridDay' && event.metadata?.type === 'AVAILABLE') continue;

            if (isMultiDay) {
                const localDate = start.toLocaleString('sv-SE', { timeZone: tz }).slice(0, 10);
                const dateColId = `date-${localDate}`;
                result.push({
                    id: event.id,
                    resourceIds: [dateColId],
                    start,
                    end,
                    title: event.title,
                    color: event.color,
                    display: event.display as 'background' | undefined,
                    className: event.className,
                    metadata: event.metadata,
                });
            } else {
                result.push({
                    id: event.id,
                    resourceIds: event.resourceIds as string[],
                    start,
                    end,
                    title: event.title,
                    color: event.color,
                    display: event.display as 'background' | undefined,
                    className: event.className,
                    metadata: event.metadata,
                });
            }
        }

        return result;
    }, [appointmentsData, viewMode, isMultiDay, tz]);

    const kaliaResources: KaliaResource[] = useMemo(() => {
        if (isMultiDay) return dateColumns;
        return filteredResources.map((r) => ({ id: r.id, title: r.title, order: r.order }));
    }, [isMultiDay, dateColumns, filteredResources]);

    const calendarConfig = useMemo(() => {
        const targetOffset = dayjs.tz(new Date(), 'Europe/Berlin').utcOffset() / 60;
        const actualOffset = dayjs.tz(new Date(), tz).utcOffset() / 60;
        const delta = actualOffset - targetOffset;

        const minHr = Math.max(0, Math.min(23, 7 + delta));
        const maxHr = Math.max(1, Math.min(24, 21 + delta));

        return {
            slotMinTime: `${minHr.toString().padStart(2, '0')}:00`,
            slotMaxTime: `${maxHr.toString().padStart(2, '0')}:00`,
            timezone: tz,
        };
    }, [tz]);

    return (
        <>
            <ScheduleEditorModal />
            <KaliaCalendar resources={kaliaResources} events={kaliaEvents} config={calendarConfig} />
        </>
    );
};

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

const ResourceCalendarNew = ({
    resources,
}: {
    resources: {
        id: string;
        title: string;
        storeId: number | null;
        storeIds: number[] | null;
        order: number;
    }[];
}) => {
    const { timezone } = useUserStore(useShallow((state) => ({ timezone: state.user?.timezone })));
    const { selectedStore } = useAppointmentsCalendarStore(useShallow((state) => ({ selectedStore: state.selectedStore })));

    return (
        <div className='relative flex flex-col gap-4 h-full'>
            <div className='flex flex-col gap-2'>
                <div className='flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2'>
                    <StoreSelector />
                    <div className='flex flex-row gap-2'>
                        <CreateAppointmentButton />
                        <CalendarScheduleTimeBlockEntry
                            userTimezone={timezone || 'Europe/Berlin'}
                            triggerButtonLabel='Neuer Blocker'
                        />
                    </div>
                </div>
                <div className='flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2'>
                    <div className='flex flex-row flex-wrap items-center gap-2'>
                        <TodayButton />
                        <CalendarDatePickerWithRange />
                        <CalendarResourcesFilter />
                        {selectedStore && <IncomingAppointmentRequestsWidget storeId={selectedStore.id} />}
                    </div>
                    <div className='flex flex-row flex-wrap items-center justify-end gap-2'>
                        <ViewModeSelector />
                    </div>
                </div>
            </div>

            <div className='flex-1 min-h-0 min-w-0'>
                <KaliaCalendarWrapper resources={resources} />
            </div>
            <div className='flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-1 px-1 pt-2'>
                <CalendarLegend />
                <ColorLegend />
            </div>
        </div>
    );
};

const AppointmentsCalendarNew = () => {
    const { setAllPractitioners, setAllRooms, setAllEquipment, selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            setAllPractitioners: state.setAllPractitioners,
            setAllRooms: state.setAllRooms,
            setAllEquipment: state.setAllEquipment,
            selectedStore: state.selectedStore,
        }))
    );

    const { data: resourcesData, isLoading: isLoadingResources } = trpc.appointments.getResources.useQuery(undefined, {
        refetchInterval: 300000,
        refetchOnWindowFocus: true,
        staleTime: 300000,
    });

    const { data: configData } = trpc.appointmentsCalendarConfig.getByStoreId.useQuery(
        { storeId: selectedStore?.id || 0 },
        { enabled: !!selectedStore?.id, refetchOnWindowFocus: false }
    );

    const resources = useMemo(() => {
        if (!resourcesData) return [];

        const config = configData?.data;
        const configResources = config?.resources || [];
        const hasConfig = configResources.length > 0;

        const resourceMap = new Map<string, any>();

        resourceMap.set('unassigned', {
            id: 'unassigned',
            title: 'Nicht zugewiesen',
            storeId: null,
            storeIds: null,
            model: 'unassigned',
            modelEntryId: null,
        });
        resourceMap.set('waiting', {
            id: 'waiting',
            title: 'Einwirkzeit / Durchlaufzeit',
            storeId: null,
            storeIds: null,
            model: 'waiting',
            modelEntryId: null,
        });
        resourceMap.set('reception', {
            id: 'reception',
            title: 'Empfang',
            storeId: null,
            storeIds: null,
            model: 'reception',
            modelEntryId: null,
        });

        (resourcesData.practitioners || []).forEach((practitioner) => {
            resourceMap.set(`practitioner-${practitioner.id}`, {
                id: `practitioner-${practitioner.id}`,
                title: `${practitioner.firstName} ${practitioner.lastName}`,
                storeId: practitioner.storeId,
                storeIds: practitioner.practitioner_store_practitioner_store_practitionerTopractitioner.map(
                    (assoc) => assoc.store_practitioner_store_storeTostore.id
                ),
                eventCount: practitioner._count.appointmentStepAssociations,
                model: 'practitioners',
                modelEntryId: practitioner.id,
            });
        });

        (resourcesData.rooms || []).forEach((room) => {
            resourceMap.set(`room-${room.id}`, {
                id: `room-${room.id}`,
                title: room.name,
                storeId: room.storeId,
                storeIds: [room.storeId],
                eventCount: room._count.appointmentStepAssociations,
                model: 'rooms',
                modelEntryId: room.id,
            });
        });

        (resourcesData.equipment || []).forEach((equipment) => {
            resourceMap.set(`equipment-${equipment.id}`, {
                id: `equipment-${equipment.id}`,
                title: equipment.name,
                storeId: equipment.storeId,
                storeIds: [equipment.storeId],
                eventCount: equipment._count.appointmentStepAssociations,
                model: 'equipment',
                modelEntryId: equipment.id,
            });
        });

        if (hasConfig) {
            const formattedResources: any[] = [];
            const addedResourceIds = new Set<string>();

            configResources.forEach((configRes) => {
                let resourceId = '';
                switch (configRes.type) {
                    case 'NOT_ASSIGNED':
                        resourceId = 'unassigned';
                        break;
                    case 'PROCESSING_TIME':
                        resourceId = 'waiting';
                        break;
                    case 'RECEPTION':
                        resourceId = 'reception';
                        break;
                    case 'PRACTITIONER':
                        if (configRes.practitioner) resourceId = `practitioner-${configRes.practitioner.id}`;
                        break;
                    case 'ROOM':
                        if (configRes.room) resourceId = `room-${configRes.room.id}`;
                        break;
                    case 'DEVICE':
                        if (configRes.device) resourceId = `equipment-${configRes.device.id}`;
                        break;
                }

                if (resourceId && resourceMap.has(resourceId)) {
                    const resource = resourceMap.get(resourceId);
                    if (
                        !selectedStore ||
                        !resource.storeIds ||
                        resource.storeIds.includes(selectedStore.id) ||
                        resource.storeId === selectedStore.id ||
                        !resource.storeId
                    ) {
                        formattedResources.push({ ...resource, order: configRes.orderIdx });
                        addedResourceIds.add(resourceId);
                    }
                }
            });

            const maxOrder = formattedResources.length > 0 ? Math.max(...formattedResources.map((r) => r.order)) : 0;
            let backfillOrder = maxOrder + 1;

            resourceMap.forEach((resource, resourceId) => {
                if (!addedResourceIds.has(resourceId)) {
                    if (
                        !selectedStore ||
                        !resource.storeIds ||
                        resource.storeIds.includes(selectedStore.id) ||
                        resource.storeId === selectedStore.id ||
                        !resource.storeId
                    ) {
                        formattedResources.push({ ...resource, order: backfillOrder++ });
                    }
                }
            });

            return formattedResources;
        }

        const sortedPractitioners = [...(resourcesData.practitioners || [])].sort((a, b) =>
            `${a.firstName} ${a.lastName}`.toLowerCase().localeCompare(`${b.firstName} ${b.lastName}`.toLowerCase())
        );
        const sortedRooms = [...(resourcesData.rooms || [])].sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
        const sortedEquipment = [...(resourcesData.equipment || [])].sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );

        return [
            { id: 'unassigned', title: 'Nicht zugewiesen', storeId: null, storeIds: null, order: -3, model: 'unassigned', modelEntryId: null },
            { id: 'waiting', title: 'Einwirkzeit / Durchlaufzeit', storeId: null, storeIds: null, order: -2, model: 'waiting', modelEntryId: null },
            { id: 'reception', title: 'Empfang', storeId: null, storeIds: null, order: -1, model: 'reception', modelEntryId: null },
            ...sortedPractitioners.map((p, i) => ({
                id: `practitioner-${p.id}`,
                title: `${p.firstName} ${p.lastName}`,
                storeId: p.storeId,
                storeIds: p.practitioner_store_practitioner_store_practitionerTopractitioner.map(
                    (assoc) => assoc.store_practitioner_store_storeTostore.id
                ),
                order: i,
                eventCount: p._count.appointmentStepAssociations,
                model: 'practitioners',
                modelEntryId: p.id,
            })),
            ...sortedRooms.map((r, i) => ({
                id: `room-${r.id}`,
                title: r.name,
                storeId: r.storeId,
                storeIds: [r.storeId],
                order: i + sortedPractitioners.length,
                eventCount: r._count.appointmentStepAssociations,
                model: 'rooms',
                modelEntryId: r.id,
            })),
            ...sortedEquipment.map((e, i) => ({
                id: `equipment-${e.id}`,
                title: e.name,
                storeId: e.storeId,
                storeIds: [e.storeId],
                order: i + sortedPractitioners.length + sortedRooms.length,
                eventCount: e._count.appointmentStepAssociations,
                model: 'equipment',
                modelEntryId: e.id,
            })),
        ];
    }, [resourcesData, configData, selectedStore]);

    useEffect(() => {
        if (resourcesData) {
            if (resourcesData.practitioners && resourcesData.practitioners.length > 0) {
                setAllPractitioners(
                    resourcesData.practitioners.map((p) => ({
                        ...p,
                        storeIds: p.practitioner_store_practitioner_store_practitionerTopractitioner.map(
                            (assoc) => assoc.store_practitioner_store_storeTostore.id
                        ),
                    }))
                );
            }
            if (resourcesData.rooms && resourcesData.rooms.length > 0) setAllRooms(resourcesData.rooms);
            if (resourcesData.equipment && resourcesData.equipment.length > 0) setAllEquipment(resourcesData.equipment);
        }
    }, [resourcesData]);

    return (
        <Content>
            <div>
                {resources.length > 0 && <ResourceCalendarNew resources={resources} />}
            </div>
        </Content>
    );
};

export default AppointmentsCalendarNew;
